import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker
} from '@react-google-maps/api';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { LocationStateContext } from './StoreLocator';
import { useMapState } from './useMapState';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import throwErrorInDev from '@jetshop/core/helpers/throwErrorInDev';

import { theme } from '../Theme';

const Wrapper = styled('div')`
  flex: 0 0 65%;
  .gm-style-moc {
    display: none;
  }

  .map-container {
    min-height: 100%;

    ${theme.below.lg} {
      height: 50vh;
    }
  }
`;

const LoadingElement = () => <div style={{ height: '100%' }}></div>;

const getCoordinates = store => {
  const storeLatitude =
    store?.coordinates?.latitude ||
    parseFloat(
      store?.description
        ?.replace('<p>', '')
        .replace('</p>', '')
        .trim()
        .split(',')[0]
        ?.replace(',', '.')
    ) ||
    '';
  const storeLongitude =
    store?.coordinates?.longitude ||
    parseFloat(
      store?.description
        ?.replace('<p>', '')
        .replace('</p>', '')
        .trim()
        .split(',')[1]
        ?.replace(',', '.')
    ) ||
    '';
  return { storeLatitude, storeLongitude };
};

const StoreMap = ({ stores, userLocation, closestStore, clickedStore }) => {
  const ref = useRef();
  const locationState = useContext(LocationStateContext);
  const { isOpen, infoId, showInfo } = useMapState();
  const [mapsLoaded, setMapsLoaded] = useState(false);

  const { googleMapsApiKey } = useShopConfig();

  throwErrorInDev(
    typeof googleMapsApiKey === 'undefined',
    'Make sure googleMapsApiKey is defined in your shop.config.js. See https://developers.google.com/maps/documentation/javascript/get-api-key'
  );

  useEffect(() => {
    if (stores && mapsLoaded) {
      // Stores are loaded, fit bounds
      const bounds = new window.google.maps.LatLngBounds();
      stores
        .filter(store => store.coordinates)
        ?.map(store =>
          bounds.extend(
            new window.google.maps.LatLng(
              store?.coordinates?.latitude,
              store?.coordinates?.longitude
            )
          )
        );

      // Don't zoom in too far on only one marker
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new window.google.maps.LatLng(
          bounds.getNorthEast().lat() + 0.01,
          bounds.getNorthEast().lng() + 0.01
        );
        const extendPoint2 = new window.google.maps.LatLng(
          bounds.getNorthEast().lat() - 0.01,
          bounds.getNorthEast().lng() - 0.01
        );
        bounds.extend(extendPoint1);
        bounds.extend(extendPoint2);
      }

      ref.current.fitBounds(bounds);
    }
  }, [mapsLoaded, stores]);

  useEffect(() => {
    if (closestStore && mapsLoaded) {
      // User has shared its location and the closest store has been calculated
      const bounds = new window.google.maps.LatLngBounds();

      bounds.extend(
        new window.google.maps.LatLng(
          userLocation.latitude,
          userLocation.longitude
        )
      );
      bounds.extend(
        new window.google.maps.LatLng(
          closestStore?.coordinates?.latitude,
          closestStore?.coordinates?.longitude
        )
      );

      ref.current.fitBounds(bounds);
    }
  }, [userLocation, closestStore, mapsLoaded]);

  useEffect(() => {
    if (clickedStore) {
      const clickedStoreObject = stores.filter(
        store => store.id === clickedStore
      );
      const storeCoordinates = getCoordinates(clickedStoreObject[0]);
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(
        new window.google.maps.LatLng(
          storeCoordinates.storeLatitude,
          storeCoordinates.storeLongitude
        )
      );

      ref.current.fitBounds(bounds);
      ref.current.setZoom(15);
    }
  }, [clickedStore, stores]);

  return (
    <Wrapper>
      <>
        <LoadScript
          loadingElement={<LoadingElement />}
          preventGoogleFontsLoading={true}
          googleMapsApiKey={googleMapsApiKey}
        >
          <GoogleMap
            defaultZoom={6}
            options={{
              styles: mapStyles,
              streetViewControl: false,
              fullscreenControl: false
            }}
            mapContainerClassName="map-container"
            defaultCenter={{ lat: 59.247948, lng: 14.755806 }}
            onLoad={map => {
              ref.current = map;
              setMapsLoaded(true);
            }}
          >
            <>
              {userLocation.latitude && userLocation.longitude && (
                <Marker
                  position={{
                    lat: userLocation.latitude,
                    lng: userLocation.longitude
                  }}
                />
              )}

              {mapsLoaded &&
                stores
                  .filter(store => store.coordinates)
                  ?.map((store, index) => {
                    const isActive =
                      store.id === locationState.activeLocation ||
                      store.id === clickedStore;

                    return (
                      <React.Fragment key={store.id}>
                        <Marker
                          icon={{
                            path:
                              'M6.2806 18.6167C0.983281 10.8 0 9.99775 0 7.125C0 3.18996 3.13399 0 7 0C10.866 0 14 3.18996 14 7.125C14 9.99775 13.0167 10.8 7.7194 18.6167C7.37177 19.1278 6.6282 19.1278 6.2806 18.6167ZM7 9.5938C8.30584 9.5938 9.41667 8.3646 9.41667 7.125C9.41667 5.8854 8.30584 4.65625 7 4.65625C5.69416 4.65625 4.58333 5.8854 4.58333 7.125C4.58333 8.3646 5.69416 9.5938 7 9.5938Z',
                            fillColor: isActive
                              ? theme.colors.white
                              : theme.colors.accent,
                            fillOpacity: 1,
                            strokeColor: isActive
                              ? theme.colors.accent
                              : 'white',
                            anchor: new window.google.maps.Point(12, 32),
                            scale: isActive ? 1.3 : 1.3,
                            labelOrigin: new window.google.maps.Point(12, 14)
                          }}
                          zIndex={index}
                          position={{
                            lat: store?.coordinates?.latitude,
                            lng: store?.coordinates?.longitude
                          }}
                          // label={{
                          //   text: `${index + 1}`,
                          //   color: isActive ? 'white' : theme.colors.accent,
                          //   fontSize: '10px',
                          //   padding: '1px'
                          // }}
                          onMouseOver={() => {
                            locationState.setHighlightedLocation(store.id);
                          }}
                          onMouseOut={() =>
                            locationState.setHighlightedLocation(null)
                          }
                          onClick={() => showInfo(store.id)}
                        />
                        {isOpen && infoId === store.id && (
                          <InfoWindow
                            key={`infowindow-${store.id}`}
                            onCloseClick={showInfo}
                            position={{
                              lat: store?.coordinates?.latitude,
                              lng: store?.coordinates?.longitude
                            }}
                            options={{
                              pixelOffset: new window.google.maps.Size(50, 0)
                            }}
                          >
                            <div
                              style={{
                                background: 'white',
                                padding: '1em',
                                minWidth: '200px',
                                fontSize: '14px',
                                lineHeight: 1.5
                              }}
                            >
                              <p>
                                <strong>{store.name}</strong>
                              </p>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: store.address1
                                }}
                              />
                            </div>
                          </InfoWindow>
                        )}
                      </React.Fragment>
                    );
                  })}
            </>
          </GoogleMap>
        </LoadScript>
      </>
    </Wrapper>
  );
};

export default StoreMap;

const mapStyles = [
  {
    featureType: 'all',
    elementType: 'geometry.fill',
    stylers: [
      {
        weight: '2.00'
      }
    ]
  },
  {
    featureType: 'all',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#9c9c9c'
      }
    ]
  },
  {
    featureType: 'all',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        color: '#f2f2f2'
      }
    ]
  },
  {
    featureType: 'landscape',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      {
        saturation: -100
      },
      {
        lightness: 45
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#eeeeee'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#7b7b7b'
      }
    ]
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'all',
    stylers: [
      {
        visibility: 'simplified'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        color: '#38b2e4'
      },
      {
        visibility: 'on'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [
      {
        // color: '#dcd7d1'
        color: '#d3d3d3'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#070707'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  }
];
